import { useCallback, useEffect, useRef, useState } from "react";
import { fetchWall, mergePosts, react, type Post } from "./wall";
import { clientId, readMine, writeMine, type ReactionCounts } from "./reactions";

const POLL_MS = 12_000;
/** Backoff ceiling — a wall this size is not worth retrying hard. */
const MAX_BACKOFF_MS = 120_000;

/**
 * Keeps the wall current without a refresh.
 *
 * Polling rather than a socket: the site is static behind Static Web Apps with
 * managed Functions, so there is no persistent connection to hold, and traffic
 * is measured in hundreds of visitors rather than thousands of concurrent
 * readers. Deltas keep each poll to a few hundred bytes.
 *
 * Inserting arrivals directly is safe here only because of the layout — the
 * compose column sits above the feed on mobile (`order: -1`) and beside it on
 * desktop, so a new post can never push the submit button down. If that
 * changes, this has to become a buffered "N new posts" control instead, or it
 * will corrupt the abandonment measurement the site exists for.
 */
export function useWallFeed() {
  const [posts, setPosts] = useState<Post[]>([]);
  const [reactions, setReactions] = useState<ReactionCounts>({});
  const [mine, setMine] = useState<Record<string, string[]>>(() => readMine());
  const [ready, setReady] = useState(false);

  /** Reactions this tab has sent but not yet seen echoed back by a poll. */
  const inFlight = useRef(0);

  /** Newest timestamp we hold, so each poll asks only for what is newer. */
  const since = useRef(0);
  const timer = useRef(0);
  const abort = useRef<AbortController | null>(null);
  const failures = useRef(0);
  const stopped = useRef(false);

  const absorb = useCallback((incoming: Post[], counts: ReactionCounts) => {
    if (incoming.length) {
      since.current = Math.max(since.current, ...incoming.map((p) => p.at));
    }
    setPosts((current) => mergePosts(current, incoming));
    // A poll that started before this tab's own tap would roll the count back
    // and make the button flicker. Let the optimistic value stand until the
    // write has been acknowledged.
    if (inFlight.current === 0) setReactions(counts);
  }, []);

  /**
   * Toggle one reaction. Applied locally first so the tap feels immediate, and
   * rolled back if the write fails — the alternative is a control that appears
   * to do nothing for up to a full poll interval.
   */
  const toggleReaction = useCallback(
    async (postId: string, emoji: string) => {
      const already = (mine[postId] ?? []).includes(emoji);
      const on = !already;

      setReactions((current) => {
        const post = { ...(current[postId] ?? {}) };
        post[emoji] = Math.max(0, (post[emoji] ?? 0) + (on ? 1 : -1));
        if (post[emoji] === 0) delete post[emoji];
        return { ...current, [postId]: post };
      });

      const nextMine = { ...mine };
      const list = new Set(nextMine[postId] ?? []);
      if (on) list.add(emoji);
      else list.delete(emoji);
      if (list.size) nextMine[postId] = [...list];
      else delete nextMine[postId];
      setMine(nextMine);
      writeMine(nextMine);

      inFlight.current += 1;
      const ok = await react(postId, emoji, clientId(), on);
      inFlight.current -= 1;

      if (!ok) {
        // Put it back the way it was rather than leave a count that never happened.
        setReactions((current) => {
          const post = { ...(current[postId] ?? {}) };
          post[emoji] = Math.max(0, (post[emoji] ?? 0) + (on ? -1 : 1));
          if (post[emoji] === 0) delete post[emoji];
          return { ...current, [postId]: post };
        });
        setMine(mine);
        writeMine(mine);
      }
    },
    [mine],
  );

  /** Local insert after a successful submit, so it lands without waiting. */
  const addLocal = useCallback((post: Post) => {
    since.current = Math.max(since.current, post.at);
    setPosts((current) => mergePosts(current, [post]));
  }, []);

  const poll = useCallback(async () => {
    abort.current?.abort();
    const ctrl = new AbortController();
    abort.current = ctrl;
    try {
      const snap = await fetchWall(since.current, ctrl.signal);
      absorb(snap.posts, snap.reactions);
      failures.current = 0;
    } catch (err) {
      // An aborted poll is a deliberate cancel, not a failure to back off from.
      if ((err as Error)?.name !== "AbortError") failures.current += 1;
    } finally {
      setReady(true);
    }
  }, [absorb]);

  useEffect(() => {
    const schedule = () => {
      window.clearTimeout(timer.current);
      if (stopped.current || document.hidden) return;
      const delay = Math.min(POLL_MS * 2 ** failures.current, MAX_BACKOFF_MS);
      timer.current = window.setTimeout(async () => {
        await poll();
        schedule();
      }, delay);
    };

    const onVisibility = () => {
      if (document.hidden) {
        window.clearTimeout(timer.current);
        abort.current?.abort();
        return;
      }
      // Catch up immediately rather than making someone who just switched back
      // wait out a full interval to see what they missed.
      void poll().then(schedule);
    };

    void poll().then(schedule);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      stopped.current = true;
      window.clearTimeout(timer.current);
      abort.current?.abort();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [poll]);

  return { posts, reactions, mine, ready, addLocal, toggleReaction };
}
